import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { type PriceData, type WithSurcharge } from "~/domains/pricing/types/PriceData";
import * as PriceUtils from "~/utils/PriceUtils";
import { type AliasesProps, getAliases, ProductAliases } from "~/domains/product/components/ProductAliases";
import {
  type FifteenDigitEntry,
  isKITKITProduct,
  isKITProduct,
  isLinearUnit,
  type PackagingInformationEntry,
  type ProductData,
} from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useProductContext, useProductDescriptorContext } from "~/domains/product/ProductContext";
import {
  userIsConsumerDistributor,
  userIsCustomer,
  userIsDistributor,
  userIsPublic,
  userIsRegistered,
} from "~/domains/user/utils/roles";
import {
  getCountriesListFromFifteendigit,
  getQtyInStock,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { QuantityStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { PopperTooltip } from "~/common/components/Tooltip";
import { getPackagingLabel } from "~/features/cart/cart/utils/cartUtils";
import { canSeeCountryOrigin } from "~/utils/siteModes";
import { uniqueValues } from "~/common/utils/misc";
import { getTariffNumbers } from "~/utils/productUtils";
import { Stack, styled } from "~/common/mui";
import { ProductParameter, type ProductParameterBaseProps } from "./shared";
import InfoIcon from "@mui/icons-material/Info";

interface BaseParamProps extends ProductParameterBaseProps {
  product: ProductData;
}

interface BaseParamWithContextProps extends Omit<BaseParamProps, "product"> {
  product?: ProductData;
}

interface BaseParamWithContextAndOwnLabelProps extends Omit<BaseParamWithContextProps, "label"> {
  product?: ProductData;
}

interface AliasesParamProps
  extends BaseParamWithContextAndOwnLabelProps,
    Omit<AliasesProps, "product" | "visibleAliasesLayout"> {
  showEmpty?: boolean;
  visibleAliasesLayout?: AliasesProps["visibleAliasesLayout"];
}

export const getVisibleAliases = ({
  product,
  mode = "all",
  packCode,
}: Pick<AliasesParamProps, "product" | "mode" | "packCode">): string[] => {
  if (!product || isKITProduct(product)) {
    return [];
  }

  return getAliases(product, mode, packCode);
};

export const shouldRenderAliasesParam = ({
  product,
  mode = "all",
  packCode,
  showEmpty = false,
}: Pick<AliasesParamProps, "product" | "mode" | "packCode" | "showEmpty">): boolean => {
  if (!product || isKITProduct(product)) {
    return false;
  }

  return showEmpty || getVisibleAliases({ product, mode, packCode }).length > 0;
};

export const AliasesParam = ({
  product: external,
  mode = "all",
  packCode,
  maxAliasesVisible,
  visibleAliasesLayout,
  showEmpty,
  ...rest
}: AliasesParamProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });
  const aliases = getVisibleAliases({ product, mode, packCode });
  if (aliases.length === 0) {
    if (!showEmpty) {
      return null;
    }

    return <ProductParameter label={t("product.customerId")} value="-" {...rest} />;
  }

  return (
    <ProductParameter label={t("product.customerId")} {...rest}>
      <ProductAliases
        product={product}
        mode={mode}
        packCode={packCode}
        maxAliasesVisible={maxAliasesVisible}
        visibleAliasesLayout={visibleAliasesLayout}
      />
    </ProductParameter>
  );
};

export const shouldRenderSchaefflerIdParam = (productDescriptor?: ProductDescriptor) =>
  !!productDescriptor &&
  !userIsPublic() &&
  !userIsRegistered() &&
  !isProductPMA(productDescriptor) &&
  !isKITKITProduct(productDescriptor.base);

export const SchaefflerIdParam = ({
  productDescriptor: external,
  ...rest
}: Omit<ProductParameterBaseProps, "label"> & { productDescriptor?: ProductDescriptor }) => {
  const { t } = useTranslation();
  const productDescriptor = useProductDescriptorContext({ productDescriptor: external });

  if (!shouldRenderSchaefflerIdParam(productDescriptor)) {
    return null;
  }

  return (
    <ProductParameter label={t("product.schaefflerId")} value={productDescriptor.base.sapMaterialNumber} {...rest} />
  );
};

export const shouldRenderOldDesignationParam = (product?: ProductData) =>
  product?.oldDesignation && !isKITKITProduct(product);

export const OldDesignationParam = ({ product: external, ...rest }: BaseParamWithContextAndOwnLabelProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (!shouldRenderOldDesignationParam(product)) {
    return null;
  }

  return <ProductParameter label={t("product.oldDesignations")} value={product.oldDesignation} {...rest} />;
};

export const LinearConfigParam = ({ product: external, ...rest }: BaseParamWithContextAndOwnLabelProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (!isLinearUnit(product)) {
    return null;
  }

  return (
    <ProductParameter label={t("cartPage.systemLayout.label")} {...rest}>
      <span>
        {t("cartPage.guidway.info", { count: Number(product.guideway) })}
        <br />
        {t("cartPage.carriages.info", { count: Number(product.carriages) })}
      </span>
    </ProductParameter>
  );
};

interface CountryOfOriginDescriptorParamProps extends Omit<ProductParameterBaseProps, "label"> {
  descriptor?: ProductDescriptor;
  packCode?: string;
  showEmpty?: boolean;
  withoutLabel?: boolean;
}

export const shouldRenderCountryOfOriginDescriptorParam = ({
  descriptor,
  packCode,
  showEmpty = false,
  withoutLabel = false,
}: {
  descriptor?: ProductDescriptor;
  packCode?: string;
  showEmpty?: boolean;
  withoutLabel?: boolean;
}): boolean => {
  if (!descriptor || isKITKITProduct(descriptor.base)) {
    return false;
  }

  const countries = getCountriesListFromFifteendigit({ descriptor, packCode });
  if (!canSeeCountryOrigin() || countries.length === 0) {
    return showEmpty && !withoutLabel;
  }

  return true;
};

export const CountryOfOriginDescriptorParam = ({
  descriptor: external,
  packCode,
  showEmpty,
  withoutLabel = false,
  ...rest
}: CountryOfOriginDescriptorParamProps) => {
  const { t } = useTranslation();
  const descriptor = useProductDescriptorContext({ productDescriptor: external });
  const countries = getCountriesListFromFifteendigit({ descriptor, packCode });
  const label = t("common.product.countryOfOrigin");

  if (!shouldRenderCountryOfOriginDescriptorParam({ descriptor, packCode, showEmpty, withoutLabel })) {
    return null;
  }

  if (!canSeeCountryOrigin() || countries.length === 0) {
    if (!showEmpty || withoutLabel) {
      return null;
    }

    return <ProductParameter label={label} value="-" {...rest} />;
  }

  const lastIndex = countries.length - 1;

  if (withoutLabel) {
    return (
      <Stack direction="row" gap={0.5} alignItems="center">
        <PublicOutlinedIcon fontSize="small" />
        <div data-test-id={rest["data-test-id"] ?? "countryOfOrigin"}>
          {countries.map((country, index) => (
            <Fragment key={country}>
              {t(`country.${country.toLowerCase()}`)}
              {index !== lastIndex && ", "}
            </Fragment>
          ))}
        </div>
      </Stack>
    );
  }

  return (
    <ProductParameter label={label} {...rest}>
      <div data-test-id={rest["data-test-id"] ?? "countryOfOrigin"}>
        {countries.map((country, index) => (
          <Fragment key={country}>
            <PopperTooltip
              style={{ display: "inline" }}
              tooltipContent={<span className="f-no-wrap">{t(`country.${country.toLowerCase()}`)}</span>}
            >
              {country}
            </PopperTooltip>
            {index !== lastIndex && ", "}
          </Fragment>
        ))}
      </div>
    </ProductParameter>
  );
};

export const shouldRenderGPSDParam = (product?: ProductData) =>
  product && !isKITKITProduct(product) && (userIsDistributor() || userIsConsumerDistributor()) && product.gpsdGroup;

export const GPSDParam = ({ product: external, ...rest }: BaseParamWithContextAndOwnLabelProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (!shouldRenderGPSDParam(product)) {
    return null;
  }

  return <ProductParameter label={t("common.product.gpsdGroup")} value={product.gpsdGroup} {...rest} />;
};

export const IncludedSurchargeParam = ({
  value,
  percent,
  calculateFrom,
  ...rest
}: Omit<ProductParameterBaseProps, "label" | "value"> & {
  value?: PriceData;
  percent?: number;
  calculateFrom?: PriceData & WithSurcharge;
}) => {
  const { t } = useTranslation();

  let nextValue = value;
  let nextPercent = percent;

  if (calculateFrom) {
    nextPercent = calculateFrom.surcharge;
    const rawValue = calculateFrom.value - calculateFrom.baseValue;
    if (rawValue > 0) {
      nextValue = {
        value: rawValue,
        currencyIso: calculateFrom.currencyIso,
      };
    }
  }

  if (!nextValue) {
    return null;
  }

  const text =
    nextPercent && nextPercent > 0
      ? `${PriceUtils.text2(nextValue, "-")} (${nextPercent * 100}%)`
      : PriceUtils.text2(nextValue, "-");

  return <ProductParameter label={t("cart.param.includedSurcharge")} value={text} {...rest} />;
};

export const shouldRenderTariffParam = (product?: ProductData): boolean => {
  if (!product || isKITKITProduct(product)) {
    return false;
  }

  if (!userIsCustomer() || !product.fifteenDigit) {
    return false;
  }

  return getTariffNumbers(product).length > 0;
};

export const TariffParam = ({ product: external, ...rest }: BaseParamWithContextAndOwnLabelProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });
  const tariffNumbers = getTariffNumbers(product);

  if (!shouldRenderTariffParam(product)) {
    return null;
  }

  return <ProductParameter label={t("common.product.tariff")} value={tariffNumbers.join(", ")} {...rest} />;
};

interface EANParamProps extends Omit<BaseParamProps, "label"> {
  packCode?: string;
  showEmpty?: boolean;
}

export const EANParam = ({ product, packCode, showEmpty, ...rest }: EANParamProps) => {
  const { t } = useTranslation();
  const label = t("common.product.ean");

  if (isKITKITProduct(product)) {
    return null;
  }

  if (!product.fifteenDigit || Object.values(product.fifteenDigit).length === 0) {
    if (!showEmpty) {
      return null;
    }

    return <ProductParameter label={label} value={<div data-test-id="ean">-</div>} {...rest} />;
  }

  let eans: string[] = [];
  if (packCode !== undefined && packCode.trim().length > 0) {
    const packageData: FifteenDigitEntry = product.fifteenDigit[packCode];
    if (packageData?.schaefflerEan) {
      eans.push(packageData.schaefflerEan);
    }
  } else {
    eans = uniqueValues(Object.values(product.fifteenDigit).map((digit) => digit.schaefflerEan)) || [];
  }

  if (eans.length === 0) {
    if (!showEmpty) {
      return null;
    }

    return <ProductParameter label={label} value={<div data-test-id="ean">-</div>} {...rest} />;
  }

  return <ProductParameter label={label} value={<div data-test-id="ean">{eans.join(", ")}</div>} {...rest} />;
};

interface StockAvailabilityProps extends Omit<ProductParameterBaseProps, "label"> {
  productDescriptor: ProductDescriptor;
  packCode: string;
}

export const StockAvailabilityParam = ({ productDescriptor, packCode, ...rest }: StockAvailabilityProps) => {
  const { t } = useTranslation();
  const inStockQty = getQtyInStock(productDescriptor, packCode);
  const showAvailability = !isProductPMA(productDescriptor) && productDescriptor.base.quantityVisible;

  return (
    <ProductParameter label={t("common.product.availability")} {...rest}>
      <>
        {showAvailability && <QuantityStockIndicator quantity={inStockQty} />}
        {!showAvailability && "-"}
      </>
    </ProductParameter>
  );
};

interface PackagingParamProps extends Omit<ProductParameterBaseProps, "label"> {
  packagingInfo: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
}

export const PackagingParam = ({ packagingInfo, productDescriptor, ...rest }: PackagingParamProps) => {
  const { t } = useTranslation();
  const packCode = packagingInfo.packCode;
  const text = getPackagingLabel({
    t,
    productDescriptor,
    packSize: packagingInfo.packSize,
    packCode,
  });

  return (
    <ProductParameter label={t("quoteDetailsPage.summary.packagingCode")} {...rest}>
      <div className="d-flex">
        {text}
        {!!packagingInfo.preferredPackQuantity && packagingInfo.preferredPackQuantity > 0 && (
          <PopperTooltip
            zIndex={1300}
            className="scha__atc-packaging-opts__pack-size__info"
            tooltipContent={
              <div className="f-no-wrap">
                {t("addToCartPopup.prefPackQuantity", { count: packagingInfo.preferredPackQuantity })}
              </div>
            }
          >
            <StyledInfoIcon />
          </PopperTooltip>
        )}
      </div>
    </ProductParameter>
  );
};

const StyledInfoIcon = styled(InfoIcon)({
  marginLeft: "8px",
  display: "flex",
  alignItems: "center",
  fontSize: "16px",
  height: "16px",
});
